import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { useSettings } from '@/context/SettingsContext';
import { Waves, Umbrella, Info } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const PoolPage = () => {
  const { currentLanguage, t } = useLanguage();
  const { settings } = useSettings();

  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPools = async () => {
      try {
        const response = await axios.get(`${API}/pools`);
        setPools(response.data);
      } catch (error) {
        console.error('Error fetching pools:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPools();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-holiday-purple border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="pool-page">
      {/* Hero */}
      <section
        className="relative h-[60vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `url('/images/general/general_3.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-holiday-purple/60" />
        <div className="relative z-10 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-4">{t('pool.title')}</h1>
          <p className="text-xl text-white/80">{t('pool.subtitle')}</p>
        </div>
      </section>

      {/* Pools Section */}
      <section className="section-padding bg-white" data-testid="pools-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Waves className="w-12 h-12 text-holiday-purple mx-auto mb-4" />
            <h2 className="text-4xl font-heading font-bold text-gray-900">
              {t('pool.pools.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pools.map((pool, index) => {
              const name = pool.name?.[currentLanguage] || pool.name?.['tr'] || '';
              return (
                <div
                  key={pool.id || index}
                  className="bg-gray-50 p-6 rounded-lg text-center hover:shadow-lg transition-shadow"
                  data-testid={`pool-${index}`}
                >
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                    pool.type === 'indoor' || pool.type === 'kids' ? 'bg-holiday-green/10' : 'bg-holiday-purple/10'
                  }`}>
                    <Waves className={`w-8 h-8 ${
                      pool.type === 'indoor' || pool.type === 'kids' ? 'text-holiday-green' : 'text-holiday-purple'
                    }`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{name}</h3>
                  <div className="flex justify-center gap-4 text-sm text-gray-500">
                    <span>{pool.size}</span>
                    <span>•</span>
                    <span>{pool.depth}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-center gap-2 mt-8 text-gray-500">
            <Info className="w-4 h-4" />
            <span className="text-sm">{t('pool.pools.note')}</span>
          </div>
        </div>
      </section>

      {/* Beach Section */}
      <section className="section-padding bg-gray-50" data-testid="beach-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-xl">
              <img
                src="/images/general/general_4.jpg"
                alt="Beach"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-holiday-green/10 rounded-full flex items-center justify-center">
                  <Umbrella className="w-6 h-6 text-holiday-green" />
                </div>
                <div>
                  <h2 className="text-3xl font-heading font-bold text-gray-900">
                    {t('pool.beach.title')}
                  </h2>
                  <p className="text-holiday-green">{t('pool.beach.subtitle')}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-1">Konum</h4>
                  <p className="text-gray-600">{t('pool.beach.location')}</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-1">Özellikler</h4>
                  <p className="text-gray-600">{t('pool.beach.features')}</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-1">Hizmetler</h4>
                  <p className="text-gray-600">{t('pool.beach.services')}</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-1">Atmosfer</h4>
                  <p className="text-gray-600">{t('pool.beach.atmosphere')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-holiday-purple text-white text-center">
        <div className="container-custom">
          <h2 className="text-4xl font-heading font-bold mb-4">
            {t('pool.beach.subtitle')}
          </h2>
          <p className="text-white/80 mb-8">{t('hero.concept')}</p>
          <a
            href={settings.reservation_url || "https://www.ihg.com"}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-accent inline-block rounded-sm"
            data-testid="pool-cta"
          >
            {t('nav.bookNow')}
          </a>
        </div>
      </section>
    </div>
  );
};

export default PoolPage;
